<template>
  <div class="page-wrapper">
    <div class="content container-fluid">

      <!-- HEADER -->
      <div class="page-header">
        <div class="row align-items-center w-100">
          <div class="col-sm-8 d-flex align-items-center flex-wrap">
            <div>
              <h3 class="page-title mb-1 d-flex align-items-center">
                <i class="fa fa-warehouse mr-2 text-primary"></i>
                Dashboard Trưởng Kho
              </h3>
              <ul class="breadcrumb small mb-0">
                <li class="breadcrumb-item"><router-link to="/">Dashboard</router-link></li>
                <li class="breadcrumb-item active">Quản lý Đơn hàng Kho</li>
              </ul>
            </div>
          </div>

          <div class="col-sm-4 text-right">
            <div class="btn-group mr-2">
              <button
                class="btn btn-outline-secondary btn-sm"
                :class="{ active: viewMode==='table' }"
                @click="viewMode='table'"
              >
                <i class="fa fa-list mr-1"></i> Bảng
              </button>
              <button
                class="btn btn-outline-secondary btn-sm"
                :class="{ active: viewMode==='card' }"
                @click="viewMode='card'"
              >
                <i class="fa fa-th-large mr-1"></i> Card
              </button>
              <button
                class="btn btn-outline-secondary btn-sm"
                :class="{ active: viewMode==='kanban' }"
                @click="viewMode='kanban'"
              >
                <i class="fa fa-columns mr-1"></i> Kanban
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- TABS / FILTER BY STATUS -->
      <div class="card card-tabs mb-3">
        <div class="card-header p-0 border-bottom-0">
          <div class="nav-tabs-scrollable">
            <ul class="nav nav-tabs nav-tabs-bottom">
              <li class="nav-item" v-for="tab in tabs" :key="tab.value">
                <a
                  class="nav-link"
                  href="#"
                  :class="{ active: filterTrangThai === tab.value }"
                  @click.prevent="setFilter(tab.value)"
                >
                  <i v-if="tab.icon" :class="['mr-1', tab.icon]"></i>{{ tab.label }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- LIST AREA -->
      <div class="card">
        <div class="card-body p-0">

          <!-- EMPTY/ERROR/LOADING STATES -->
          <div v-if="isLoadingList && items.length === 0" class="p-4 text-center">Đang tải...</div>
          <div v-else-if="listError && items.length === 0" class="alert alert-danger m-3">{{ listError }}</div>
          <div v-else-if="items.length === 0" class="p-4 text-center text-muted">Không có đơn hàng nào.</div>

          <!-- TABLE MODE -->
          <div v-else-if="viewMode==='table'" class="table-responsive">
            <table class="table table-hover table-striped mb-0">
              <thead class="thead-light">
                <tr>
                  <th>Tên Công trình / Đơn hàng</th>
                  <th>NV Sales</th>
                  <th>Ghi chú Sales / File</th>
                  <th>Ghi chú Kho</th>
                  <th>Trạng thái</th>
                  <th class="text-right">Hành động</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in items"
                  :key="item.id"
                >
                  <td @click="handleOpenDetail(item.id)" style="cursor:pointer;">
                    <strong class="d-block text-wrap">{{ item.tenDonHang }}</strong>
                    <small class="text-muted d-block text-wrap" style="max-width:320px;">
                      <i class="fa fa-map-marker text-danger mr-1"></i>
                      {{ item.diaChiGiaoHang }}
                    </small>
                  </td>

                  <td class="align-middle">
                    <div class="small">
                      <i class="fa fa-user mr-1 text-secondary"></i>
                      {{ item.tenNguoiTao || '-' }}
                    </div>
                    <div class="text-muted small">
                      {{ item.ngayTao || '' }}
                    </div>
                  </td>

                  <td class="align-middle" style="min-width:200px; max-width:260px;">
                    <div v-if="item.ghiChuSales" class="mb-1">
                      <pre class="small-pre text-wrap" :title="item.ghiChuSales">{{ item.ghiChuSales }}</pre>
                    </div>
                    <div class="d-flex align-items-center flex-wrap">
                      <button
                        v-if="item.fileUrl"
                        class="btn btn-xs btn-outline-info mr-1 mb-1"
                        @click="previewImage(item.fileUrl)"
                      >
                        <i class="fa fa-image"></i> Xem
                      </button>
                      <a
                        v-if="item.fileUrl"
                        :href="item.fileUrl"
                        target="_blank"
                        rel="noopener"
                        class="btn btn-xs btn-info mb-1"
                      >
                        <i class="fa fa-external-link"></i>
                      </a>
                    </div>
                  </td>

                  <td class="text-danger font-weight-bold align-middle" style="min-width:180px; max-width:220px;">
                    <pre class="small-pre text-wrap" :title="item.ghiChuKho">{{ item.ghiChuKho }}</pre>
                  </td>

                  <td class="align-middle">
                    <span class="badge" :class="getOrderStatusBadge(item.trangThai)">
                      {{ formatOrderStatus(item.trangThai) }}
                    </span>
                  </td>

                  <td class="text-right align-middle">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="handleOpenDetail(item.id)"
                    >
                      <i class="fa fa-eye"></i> Xem / Xử lý
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- CARD MODE -->
          <div v-else-if="viewMode==='card'" class="p-3">
            <div class="row">
              <div
                class="col-md-6 col-xl-4 mb-3"
                v-for="item in items"
                :key="item.id"
              >
                <div
                  class="card shadow-sm h-100 border hover-card cursor-pointer"
                  @click="handleOpenDetail(item.id)"
                >
                  <div class="card-body">
                    <div class="d-flex justify-content-between mb-2">
                      <div class="font-weight-bold text-truncate" style="max-width:70%">
                        {{ item.tenDonHang }}
                      </div>
                      <div class="text-right">
                        <span class="badge mr-1" :class="getOrderStatusBadge(item.trangThai)">
                          {{ formatOrderStatus(item.trangThai) }}
                        </span>
                      </div>
                    </div>

                    <div class="small text-muted mb-2">
                      <i class="fa fa-map-marker text-danger mr-1"></i>
                      {{ item.diaChiGiaoHang || '-' }}
                    </div>

                    <div class="mb-2">
                      <div class="d-flex align-items-start small flex-wrap">
                        <div class="mr-3 mb-1">
                          <div class="text-muted">Sales</div>
                          <div class="font-weight-bold">{{ item.tenNguoiTao || '-' }}</div>
                        </div>
                        <div class="mb-1" style="min-width:100px;">
                          <div class="text-muted">Trạng thái</div>
                          <span class="badge" :class="getOrderStatusBadge(item.trangThai)">
                            {{ formatOrderStatus(item.trangThai) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="small">
                      <div v-if="item.ghiChuSales" class="mb-1">
                        <div class="text-muted">Ghi chú Sales:</div>
                        <pre class="card-pre mb-1">{{ item.ghiChuSales }}</pre>
                      </div>

                      <div v-if="item.ghiChuKho" class="mb-1">
                        <div class="text-danger font-weight-bold">Kho ghi chú:</div>
                        <pre class="card-pre text-danger mb-0">{{ item.ghiChuKho }}</pre>
                      </div>

                      <div class="mt-2 d-flex flex-wrap">
                        <button
                          v-if="item.fileUrl"
                          class="btn btn-xs btn-outline-info mr-2 mb-2"
                          @click.stop="previewImage(item.fileUrl)"
                        >
                          <i class="fa fa-image"></i> Ảnh/phiếu
                        </button>
                        <a
                          v-if="item.fileUrl"
                          :href="item.fileUrl"
                          target="_blank"
                          rel="noopener"
                          class="btn btn-xs btn-info mb-2"
                          @click.stop
                        >
                          <i class="fa fa-external-link"></i> Mở
                        </a>
                      </div>
                    </div>
                  </div>

                  <div class="card-footer text-right small text-muted">
                    <i class="fa fa-clock-o mr-1"></i>
                    {{ item.ngayTao || '---' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- KANBAN MODE -->
          <div v-else-if="viewMode==='kanban'" class="kanban-wrapper p-3">
            <div class="kanban-scroll">
              <div
                class="kanban-col"
                v-for="col in kanbanColumns"
                :key="col.key"
              >
                <div class="kanban-header">
                  <div class="font-weight-bold d-flex align-items-center">
                    <span class="kanban-dot mr-2" :class="'dot-'+col.key"></span>
                    {{ col.title }}
                  </div>
                  <div class="badge badge-light">{{ groupedByStatus[col.key]?.length || 0 }}</div>
                </div>

                <div class="kanban-body">
                  <div
                    v-for="item in groupedByStatus[col.key]"
                    :key="item.id"
                    class="kanban-card"
                    @click="handleOpenDetail(item.id)"
                  >
                    <div class="d-flex justify-content-between mb-1">
                      <div class="font-weight-bold text-truncate" style="max-width:70%">
                        {{ item.tenDonHang }}
                      </div>
                      <span class="badge badge-light text-nowrap">
                        {{ item.tenNguoiTao || 'Sales ?' }}
                      </span>
                    </div>

                    <div class="small text-muted text-truncate mb-2">
                      <i class="fa fa-map-marker text-danger mr-1"></i>
                      {{ item.diaChiGiaoHang || '-' }}
                    </div>

                    <div class="small">
                      <div v-if="item.ghiChuKho" class="text-danger font-weight-bold text-truncate-2">
                        {{ item.ghiChuKho }}
                      </div>
                      <div v-if="item.ghiChuSales" class="text-muted text-truncate-2">
                        {{ item.ghiChuSales }}
                      </div>
                    </div>

                    <div class="kanban-footer small text-muted mt-2 d-flex flex-wrap justify-content-between">
                      <div class="mr-2">
                        <i class="fa fa-clock-o mr-1"></i>{{ item.ngayTao || '---' }}
                      </div>

                      <div v-if="item.fileUrl">
                        <button
                          class="btn btn-xxs btn-outline-info"
                          @click.stop="previewImage(item.fileUrl)"
                        >
                          <i class="fa fa-image"></i> Ảnh
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="!groupedByStatus[col.key] || groupedByStatus[col.key].length===0"
                    class="kanban-empty small text-muted text-center py-3"
                  >
                    (Không có đơn)
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- fallback -->
          <div v-else class="p-4 text-center text-muted">Không có chế độ hiển thị hợp lệ.</div>

        </div>
      </div>

      <!-- MODAL: XỬ LÝ ĐƠN -->
      <div
        v-if="showModal"
        class="modal fade show d-block modal-overlay"
        tabindex="-1"
      >
        <div class="modal-dialog modal-lg modal-full-mobile" role="document">
          <div class="modal-content kho-modal-wrapper">

            <div class="modal-header bg-primary text-white align-items-start">
              <div class="w-100">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="mr-2">
                    <div class="h5 font-weight-bold mb-1 d-flex align-items-center">
                      <i class="fa fa-pencil-square-o mr-2"></i>
                      Xử lý Đơn hàng
                    </div>
                    <div class="small text-white-50">
                      {{ currentOrder?.tenDonHang || '---' }}
                    </div>
                  </div>

                  <button
                    type="button"
                    class="close text-white ml-auto p-2"
                    @click="closeModal"
                  >
                    <span>&times;</span>
                  </button>
                </div>

                <div v-if="currentOrder" class="mt-2 d-flex flex-wrap align-items-center">
                  <span class="badge badge-light text-dark mr-2 mb-1">
                    <i class="fa fa-map-marker text-danger mr-1"></i>
                    {{ currentOrder.diaChiGiaoHang || '---' }}
                  </span>

                  <span class="badge mr-2 mb-1" :class="getOrderStatusBadge(currentOrder.trangThai)">
                    {{ formatOrderStatus(currentOrder.trangThai) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="modal-body">

              <div v-if="isLoadingDetail" class="text-center my-4">
                <div class="spinner-border"></div>
                <div class="text-muted small mt-2">Đang tải chi tiết...</div>
              </div>

              <div v-else-if="detailError" class="alert alert-danger">
                {{ detailError }}
              </div>

              <div v-else-if="currentOrder">

                <!-- 1. THÔNG TIN SALES -->
                <div class="section-block">
                  <div class="section-header">
                    <span class="section-index bg-primary text-white">1</span>
                    <span class="section-title">Thông tin từ Sales</span>
                  </div>

                  <div class="info-row align-items-start">
                    <div class="info-label">Ghi chú Sales</div>
                    <div class="info-value">
                      <pre class="pre-block mb-2">{{ currentOrder.ghiChuSales || '-' }}</pre>
                      <div class="small text-muted">
                        Sales: <strong>{{ currentOrder.tenNguoiTao || '-' }}</strong>
                      </div>
                    </div>
                  </div>

                  <div class="info-row align-items-start">
                    <div class="info-label">File / Ảnh</div>
                    <div class="info-value">
                      <div v-if="currentOrder.fileUrl">
                        <div class="d-flex flex-wrap align-items-center">
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-info mr-2 mb-2"
                            @click="previewImage(currentOrder.fileUrl)"
                          >
                            <i class="fa fa-image"></i> Xem nhanh
                          </button>                
                        </div>

                        <div
                          class="sales-image-thumb"
                          @click="previewImage(currentOrder.fileUrl)"
                        >
                          <img
                            :src="currentOrder.fileUrl"
                            class="img-fluid rounded border"
                            style="max-height:140px;object-fit:cover"
                            @error="onImgErr($event)"
                          />
                          <div class="thumb-overlay">
                            <i class="fa fa-search-plus"></i>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-muted">
                        Không có hình đính kèm.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 2. HOẠCH ĐỊNH -->
                <div
                  v-if="currentOrder.trangThai === 'CHO_HOACH_DINH'"
                  class="section-block action-block"
                >
                  <div class="section-header">
                    <span class="section-index bg-danger text-white">2</span>
                    <span class="section-title text-danger">
                      Hoạch định Lộ trình
                    </span>
                  </div>

                  <div class="small text-muted mb-2">
                    Chưa có điểm dừng. Thêm điểm dừng, gán NV Kho phụ trách.
                  </div>

                    <div class="border rounded p-2 bg-light mb-3">
                      <div class="font-weight-bold small mb-2">
                        Các điểm dừng đã thêm
                      </div>

                      <ul
                        v-if="formHoachDinh.diemDungList.length > 0"
                        class="list-group list-group-sm mb-2"
                      >
                        <li
                          v-for="(stop, index) in formHoachDinh.diemDungList"
                          :key="index"
                          class="list-group-item"
                        >
                          <div class="d-flex justify-content-between">
                            <div>
                              <strong>{{ stop.tenDiemDung }}</strong>
                              <small class="d-block text-muted">{{ stop.diaChi || '-' }}</small>
                              <small class="d-block text-info">VT: {{ stop.chiTietVatTu }}</small>
                              <small class="d-block text-primary">
                                Phụ trách: {{ getKhoNhanVienName(stop.nguoiPhuTrachKhoId) || '-' }}
                              </small>
                              <small class="d-block">
                                <span class="badge badge-secondary">{{ stop.loaiDiemDung }}</span>
                              </small>
                            </div>

                            <button
                              type="button"
                              class="btn btn-sm btn-outline-danger h-25"
                              @click="removeStop(index)"
                            >
                              <i class="fa fa-trash"></i>
                            </button>
                          </div>
                        </li>
                      </ul>

                      <div class="form-row p-2 border-top">
                        <div class="col-12">
                          <label class="small font-weight-bold">Thêm Điểm Dừng</label>
                        </div>

                        <div class="form-group col-md-6">
                          <input
                            v-model.trim="formDiemDungMoi.tenDiemDung"
                            class="form-control form-control-sm"
                            placeholder="Tên điểm dừng (VD: Kho A)"
                          />
                        </div>

                        <div class="form-group col-md-6">
                          <input
                            v-model.trim="formDiemDungMoi.diaChi"
                            class="form-control form-control-sm"
                            placeholder="Địa chỉ điểm dừng (nếu có)"
                          />
                        </div>

                        <div class="form-group col-12">
                          <input
                            v-model.trim="formDiemDungMoi.chiTietVatTu"
                            class="form-control form-control-sm"
                            placeholder="Vật tư / Ghi chú (VD: Ống thép x50)"
                          />
                        </div>

                        <div class="form-group col-md-6">
                          <label class="small">Loại điểm dừng <span class="text-danger">*</span></label>
                          <select
                            v-model="formDiemDungMoi.loaiDiemDung"
                            class="form-control form-control-sm"
                            required
                          >
                            <option value="KHO_LAY">Lấy hàng tại Kho (Tài xế lấy)</option>
                            <option value="KHACH_TU_LAY">Khách Tự Lấy (Tại Kho)</option>
                          </select>
                        </div>

                        <div class="form-group col-md-6">
                          <label class="small">NV Kho phụ trách <span class="text-danger">*</span></label>
                          <select
                            v-model="formDiemDungMoi.nguoiPhuTrachKhoId"
                            class="form-control form-control-sm"
                            required
                            :disabled="isLoadingOptions"
                          >
                            <option :value="null" disabled>-- Gán NV Kho --</option>
                            <option
                              v-for="nv in khoNhanVienOptions"
                              :key="nv.idNguoiDung"
                              :value="nv.idNguoiDung"
                            >
                              {{ nv.hoTen }}
                            </option>
                          </select>
                        </div>

                        <div class="col-12">
                          <button
                            type="button"
                            class="btn btn-sm btn-secondary"
                            @click="addStop"
                          >
                            <i class="fa fa-plus"></i> Thêm Điểm
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex flex-wrap">
                      <button
                        type="button"
                        class="btn btn-warning mr-2 mb-2"
                        @click="runBaoThieu"
                        :disabled="isLoadingDetail"
                      >
                        <i class="fa fa-exclamation-triangle"></i>
                        Báo Thiếu (Trả về Sales)
                      </button>

                      <button
                        type="button"
                        class="btn btn-primary mb-2"
                        @click="runHoachDinh"
                        :disabled="isLoadingDetail || isLoadingOptions || formHoachDinh.diemDungList.length === 0"
                      >
                        <span v-if="isLoadingDetail" class="spinner-border spinner-border-sm"></span>
                        <i class="fa fa-sitemap"></i>
                        Xác nhận Hoạch Định
                      </button>
                    </div>
                </div>

                <!-- 3. ĐANG CHUẨN BỊ -->
                <div
                  v-else-if="currentOrder.trangThai === 'DANG_CHUAN_BI_HANG'"
                  class="section-block"
                >
                  <div class="section-header">
                    <span class="section-index bg-success text-white">2</span>
                    <span class="section-title text-success">
                      Đang Chuẩn Bị Hàng
                    </span>
                  </div>

                  <div class="small text-muted mb-2">
                    Chờ NV Kho báo cáo soạn xong từng điểm dừng.
                  </div>

                  <div class="table-responsive">
                    <table class="table table-sm table-hover">
                      <thead class="thead-light">
                        <tr>
                          <th>Điểm dừng</th>
                          <th>NV Kho</th>
                          <th>Vật tư/Ghi chú</th>
                          <th>Trạng thái</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="st in currentOrder.diemDung"
                          :key="st.id"
                        >
                          <td class="align-middle">
                            <div class="font-weight-bold">{{ st.tenDiemDung || '-' }}</div>
                            <div class="text-muted small">{{ st.diaChi || '-' }}</div>
                          </td>
                          <td class="align-middle">
                            {{ st.tenNguoiPhuTrachKho || '-' }}
                          </td>
                          <td class="align-middle">
                            <div class="small text-wrap" style="max-width:220px;">
                              {{ st.chiTietVatTu || '-' }}
                            </div>
                          </td>
                          <td class="align-middle">
                            <span class="badge" :class="getStopStatusBadge(st.trangThai)">
                              {{ formatStopStatus(st.trangThai) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="border rounded p-3 bg-light mt-3">
                    <div class="font-weight-bold mb-2">
                      Đẩy vào Hàng Đợi (Sẵn sàng giao)
                    </div>
                    <div class="small text-muted mb-2">
                      Khi tất cả điểm dừng đã soạn xong → đính kèm Phiếu Xuất Kho → chuyển sang Sẵn Sàng Giao.
                    </div>

                    <div class="form-group mb-2">
                      <label class="mb-1">
                        Link Phiếu Xuất Kho
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.trim="formSanSangGiao.phieuXuatKhoUrl"
                        type="text"
                        class="form-control form-control-sm"
                        placeholder="Dán link ảnh PXK (Drive, Zalo...)"
                      />
                    </div>

                    <button
                      type="button"
                      class="btn btn-success btn-block font-weight-bold"
                      @click="runSanSangGiao"
                      :disabled="isLoadingDetail || !formSanSangGiao.phieuXuatKhoUrl"
                    >
                      <span v-if="isLoadingDetail" class="spinner-border spinner-border-sm mr-2"></span>
                      <i class="fa fa-truck mr-1"></i>
                      ĐẨY VÀO HÀNG ĐỢI
                    </button>
                  </div>
                </div>

                <!-- 4. SALES ĐANG XÁC NHẬN THIẾU -->
                <div
                  v-else-if="currentOrder.trangThai === 'CHO_SALES_XAC_NHAN_THIEU'"
                  class="section-block"
                >
                  <div class="section-header">
                    <span class="section-index bg-warning text-dark">2</span>
                    <span class="section-title text-warning">
                      Đang Báo Thiếu
                    </span>
                  </div>

                  <div class="alert alert-danger">
                    Đã gửi báo thiếu cho Sales:
                    <strong>{{ currentOrder.ghiChuKho || '(Không có ghi chú)' }}</strong><br/>
                    Đang chờ Sales xác nhận điều chỉnh đơn hàng...
                  </div>
                </div>

                <!-- 5. KHÁC -->
                <div v-else class="section-block">
                  <div class="section-header">
                    <span class="section-index bg-info text-white">2</span>
                    <span class="section-title text-info">
                      {{ formatOrderStatus(currentOrder.trangThai) }}
                    </span>
                  </div>

                  <p class="mb-0">
                    Đơn hàng hiện đang ở trạng thái
                    <strong>{{ formatOrderStatus(currentOrder.trangThai) }}</strong>.
                    Không cần xử lý thêm.
                  </p>
                </div>

              </div>
            </div>

            <div class="modal-footer modal-footer-mobile" v-if="currentOrder">
              <button
                type="button"
                class="btn btn-outline-secondary btn-block"
                @click="closeModal"
                :disabled="isLoadingDetail"
              >
                Đóng
              </button>
            </div>

            <vue-easy-lightbox
              :visible="lightboxVisible"
              :imgs="lightboxImages"
              :index="lightboxIndex"
              @hide="hideLightbox"
            />
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useKhoDonHangStore } from '@/stores/khoDonHangStore'
import { getOrderStatusBadge, formatOrderStatus } from '@/helpers/orderStatus'
import { getStopStatusBadge, formatStopStatus, formatLoaiDiemDung } from '@/helpers/stopHelpers'
import { lockBodyScroll, unlockBodyScroll } from '@/helpers/dom'
import VueEasyLightbox from 'vue-easy-lightbox'

export default {
  name: 'KhoManagerDashboard',
  components: {
    VueEasyLightbox
  },
  data () {
    return {
      viewMode: 'kanban',

      showModal: false,

      formDiemDungMoi: {
        tenDiemDung: '',
        diaChi: '',
        chiTietVatTu: '',
        loaiDiemDung: 'KHO_LAY',
        nguoiPhuTrachKhoId: null
      },
      formHoachDinh: {
        diemDungList: []
      },

      formSanSangGiao: {
        phieuXuatKhoUrl: ''
      },

      lightboxVisible: false,
      lightboxIndex: 0,
      lightboxImages: [],

      tabs: [
        { value: 'CHO_HOACH_DINH', label: 'Chờ Hoạch Định', icon: 'fa fa-sitemap' },
        { value: 'DANG_CHUAN_BI_HANG', label: 'Đang Chuẩn Bị', icon: 'fa fa-cogs' },
        { value: 'CHO_SALES_XAC_NHAN_THIEU', label: 'Chờ Sales', icon: 'fa fa-exclamation-triangle text-danger' },
        { value: 'SAN_SANG_GIAO', label: 'Sẵn Sàng Giao' },
        { value: null, label: 'Tất cả' }
      ]
    }
  },
  computed: {
    ...mapState(useKhoDonHangStore, [
      'isLoadingList', 'listError', 'items', 'totalPages', 'currentPage', 'size',
      'filterTrangThai',
      'isLoadingDetail', 'detailError', 'currentOrder',
      'isLoadingOptions', 'khoNhanVienOptions'
    ]),

    groupedByStatus () {
      const groups = {}
      this.items.forEach(o => {
        if (!groups[o.trangThai]) groups[o.trangThai] = []
        groups[o.trangThai].push(o)
      })
      return groups
    },

    kanbanColumns () {
      return [
        { key: 'CHO_HOACH_DINH', title: 'Chờ Hoạch định' },
        { key: 'DANG_CHUAN_BI_HANG', title: 'Chuẩn bị hàng' },
        { key: 'CHO_SALES_XAC_NHAN_THIEU', title: 'Chờ Sales' },
        { key: 'SAN_SANG_GIAO', title: 'Sẵn sàng giao' }
      ]
    }
  },
  methods: {
    ...mapActions(useKhoDonHangStore, [
      'fetchList',
      'setFilterTrangThai',
      'setPage',
      'openDetailModal',
      'clearDetailModal',
      'fetchKhoOptionsIfNeeded',
      'baoThieuHang',
      'hoachDinhVaChuanBi',
      'sanSangGiao'
    ]),

    getOrderStatusBadge,
    formatOrderStatus,
    getStopStatusBadge,
    formatStopStatus,
    formatLoaiDiemDung,

    previewImage (url) {
      if (!url) return
      this.lightboxImages = [url]
      this.lightboxIndex = 0
      this.lightboxVisible = true
    },
    hideLightbox () {
      this.lightboxVisible = false
    },
    onImgErr (e) {
      e.target.classList.add('img-fallback')
    },

    getKhoNhanVienName (id) {
      if (!id || !this.khoNhanVienOptions) return ''
      const nv = this.khoNhanVienOptions.find(n => n.idNguoiDung === id)
      return nv ? nv.hoTen : `ID (${id})`
    },

    setFilter (status) {
      this.setFilterTrangThai(status)
    },

    async handleOpenDetail (orderId) {
      this.showModal = true
      lockBodyScroll()

      this.formHoachDinh.diemDungList = []
      this.formSanSangGiao.phieuXuatKhoUrl = ''

      await this.openDetailModal(orderId)
    },

    closeModal () {
      this.showModal = false
      this.clearDetailModal()
      unlockBodyScroll()
    },

    addStop () {
      if (!this.formDiemDungMoi.tenDiemDung ||
          !this.formDiemDungMoi.chiTietVatTu ||
          !this.formDiemDungMoi.nguoiPhuTrachKhoId) {
        alert('Vui lòng nhập Tên Điểm Dừng, Vật tư/Ghi chú, và gán NV Kho.')
        return
      }
      this.formHoachDinh.diemDungList.push({ ...this.formDiemDungMoi })
      this.formDiemDungMoi = {
        tenDiemDung: '',
        diaChi: '',
        chiTietVatTu: '',
        loaiDiemDung: 'KHO_LAY',
        nguoiPhuTrachKhoId: null
      }
    },

    removeStop (index) {
      this.formHoachDinh.diemDungList.splice(index, 1)
    },

    async runBaoThieu () {
      const ghiChu = prompt('Nhập lý do báo thiếu (gửi cho Sales):')
      if (!ghiChu || ghiChu.trim() === '') {
        alert('Phải nhập lý do.')
        return
      }
      try {
        await this.baoThieuHang(this.currentOrder.id, ghiChu.trim())
        this.closeModal()
      } catch (e) {
        alert(this.detailError || 'Lỗi khi báo thiếu')
      }
    },

    async runHoachDinh () {
      if (this.formHoachDinh.diemDungList.length === 0) {
        alert('Bạn phải thêm ít nhất 1 điểm dừng.')
        return
      }
      if (!confirm('Xác nhận hoạch định với ' + this.formHoachDinh.diemDungList.length + ' điểm dừng?')) {
        return
      }
      try {
        const payload = {
          diemDungList: this.formHoachDinh.diemDungList
        }
        await this.hoachDinhVaChuanBi(this.currentOrder.id, payload)
        alert('Hoạch định thành công! Đã chuyển sang "Đang chuẩn bị hàng".')
      } catch (e) {
        alert(this.detailError || 'Lỗi khi hoạch định')
      }
    },

    async runSanSangGiao () {
      if (!this.formSanSangGiao.phieuXuatKhoUrl) {
        alert('Vui lòng nhập link Phiếu Xuất Kho.')
        return
      }
      if (!confirm('Xác nhận đẩy đơn hàng vào Hàng Đợi (Sẵn sàng giao)?')) {
        return
      }
      try {
        const payload = {
          phieuXuatKhoUrl: this.formSanSangGiao.phieuXuatKhoUrl
        }
        await this.sanSangGiao(this.currentOrder.id, payload)
        this.closeModal()
      } catch (e) {
        alert(this.detailError || 'Lỗi khi đẩy vào hàng đợi')
      }
    }
  },
  mounted () {
    this.setFilter(this.filterTrangThai || 'CHO_HOACH_DINH')
  }
}
</script>

<style scoped>
/* layout */
.modal-body {
  max-height: calc(100vh - 170px);
  overflow-y: auto;
}
.nav-tabs-scrollable {
  overflow-x: auto;
  white-space: nowrap;
}
.nav-tabs .nav-link {
  padding: 0.75rem 1.25rem;
}
.hover-card {
  transition: box-shadow .15s;
}
.hover-card:hover {
  box-shadow: 0 .5rem 1rem rgba(0,0,0,.15) !important;
}
.cursor-pointer {
  cursor: pointer;
}

/* pre blocks */
pre {
  white-space: pre-wrap;
  margin: 0;
  font-family: inherit;
  font-size: 0.9rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 0.5rem;
  max-height: 100px;
  overflow-y: auto;
}
pre.small-pre {
  padding: 2px 5px;
  max-height: 60px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #fff;
  border: 1px solid #f1f1f1;
  border-radius: .25rem;
  font-size: .8rem;
  line-height: 1.3;
}
.card-pre {
  white-space: pre-wrap;
  font-size: .8rem;
  line-height: 1.3;
  background: #fff;
  border: 1px solid #f1f1f1;
  border-radius: .25rem;
  padding: .5rem .5rem;
  max-height: 80px;
  overflow-y: auto;
  margin:0;
}

/* kanban */
.kanban-wrapper {
  width: 100%;
  overflow-x: auto;
}
.kanban-scroll {
  min-width: 900px;
  display: flex;
  align-items: flex-start;
}
.kanban-col {
  width: 260px;
  min-width: 260px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: .5rem;
  margin-right: 1rem;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 260px);
}
.kanban-header {
  padding: .75rem .75rem .5rem;
  border-bottom: 1px solid #dee2e6;
  background: #fff;
  border-top-left-radius: .5rem;
  border-top-right-radius: .5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.kanban-body {
  padding: .75rem;
  overflow-y: auto;
  flex: 1;
}
.kanban-card {
  background: #fff;
  border-radius: .5rem;
  border: 1px solid #dee2e6;
  box-shadow: 0 .25rem .5rem rgba(0,0,0,.05);
  padding: .75rem .75rem .5rem;
  font-size: .9rem;
  margin-bottom: .75rem;
  cursor: pointer;
}
.kanban-card:last-child {
  margin-bottom: 0;
}
.kanban-footer {
  border-top: 1px solid #f1f1f1;
  padding-top: .5rem;
}
.kanban-empty {
  border: 2px dashed #d6d6d6;
  border-radius: .5rem;
  background: #fff;
}
.kanban-dot {
  width: .6rem;
  height: .6rem;
  border-radius: 50%;
  background: #6c757d;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #fff;
}
.dot-CHO_HOACH_DINH { background:#6c757d; }
.dot-DANG_CHUAN_BI_HANG { background:#ffc107; }
.dot-CHO_SALES_XAC_NHAN_THIEU { background:#dc3545; }
.dot-SAN_SANG_GIAO { background:#28a745; }

/* modal / mobile-first detail panel */
.modal-overlay {
  background: rgba(0,0,0,.6);
}
.modal-full-mobile {
  max-width: 900px;
}
@media (max-width: 576px){
  .modal-dialog.modal-lg.modal-full-mobile {
    margin: 0;
    max-width: 100%;
    height: 100%;
  }
  .modal-content {
    min-height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }
  .modal-body {
    max-height: calc(100vh - 170px);
    overflow-y: auto;
  }
  .modal-footer-mobile {
    padding: 1rem;
    border-top: 1px solid #dee2e6;
  }
}
.kho-modal-wrapper {
  border-radius: .75rem;
  overflow: hidden;
}
.modal-header {
  border-bottom: none;
  padding: 1rem 1rem .75rem 1rem;
}
.modal-body {
  background: #f8f9fa;
  padding: 1rem;
}
.modal-footer {
  border-top: none;
  padding: .75rem 1rem 1rem 1rem;
}

/* section blocks */
.section-block {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: .5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: .75rem;
  flex-wrap: wrap;
}
.section-index {
  display: inline-block;
  min-width: 1.8rem;
  height: 1.8rem;
  line-height: 1.8rem;
  border-radius: .4rem;
  font-size: .8rem;
  font-weight: 600;
  text-align: center;
  margin-right: .5rem;
}
.section-title {
  font-weight: 600;
  font-size: .95rem;
}

/* info rows in modal */
.info-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  padding: .5rem 0;
  border-bottom: 1px dashed #e1e1e1;
  font-size: .9rem;
}
.info-row:last-child {
  border-bottom: 0;
}
.info-label {
  flex: 0 0 120px;
  max-width: 120px;
  color: #6c757d;
  font-size: .8rem;
  line-height: 1.3;
  font-weight: 500;
}
.info-value {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  font-size: .9rem;
  line-height: 1.4;
}
@media (max-width: 576px){
  .info-row {
    flex-wrap: wrap;
  }
  .info-label {
    flex: 0 0 100%;
    max-width: 100%;
    font-size: .75rem;
    margin-bottom: .25rem;
  }
  .info-value {
    flex: 0 0 100%;
    max-width: 100%;
    font-size: .9rem;
    background: #f8f9fa;
    border: 1px solid #e5e7eb;
    border-radius: .4rem;
    padding: .5rem .6rem;
  }
}

/* image preview thumb in modal */
.sales-image-thumb {
  position: relative;
  display: inline-block;
  cursor: zoom-in;
  max-width: 180px;
}
.sales-image-thumb img {
  border-radius: .5rem;
  max-width: 180px;
  max-height: 140px;
  object-fit: cover;
}
.sales-image-thumb .thumb-overlay {
  position: absolute;
  right: .5rem;
  bottom: .5rem;
  background: rgba(0,0,0,.6);
  color: #fff;
  font-size: .8rem;
  line-height: 1;
  padding: .3rem .4rem;
  border-radius: .4rem;
}

.btn-xs {
  font-size: .7rem;
  line-height: 1.2;
  padding: .25rem .4rem;
}
.btn-xxs {
  font-size: .7rem;
  line-height: 1.1;
  padding: .2rem .4rem;
}
.text-truncate-2 {
  max-height: 2.6em;
  line-height: 1.3em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.img-fallback {
  object-fit: contain !important;
  background: #f1f1f1;
}
</style>
